/*
 * DREPEEZ upgrades: smooth jumps to sections, quick add to cart, and a wishlist saved in this
 * browser (localStorage; no account or app). Config comes from window.DZ (snippets/dz-head.liquid).
 */
(() => {
  const DZ = window.DZ || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Helpers ---------- */

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return (DZ.moneyFormat || 'Dhs. {{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return Math.round(cents / 100).toLocaleString('en');
      if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
      return amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    });
  };

  const escapeHtml = (text) =>
    String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const parse = (text) => new DOMParser().parseFromString(text, 'text/html');

  const heartSvg =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.3s-7.4-4.5-9.2-9C1.6 8 3.4 4.6 6.9 4.6c2 0 3.6 1.1 5.1 3 1.5-1.9 3.1-3 5.1-3 3.5 0 5.3 3.4 4.1 6.7-1.8 4.5-9.2 9-9.2 9z"/></svg>';
  const checkSvg = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

  /* ---------- Toast ---------- */

  let toast;
  let toastTimer;
  const showToast = (html) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dz-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML = html;
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  /* ---------- Smooth jumps to sections on the same page ---------- */

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href*="#"]');
    if (!link || link.target === '_blank') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    history.pushState(null, '', url.hash);
  });

  /* ---------- Cart refresh after a quick add ---------- */

  const refreshCart = async () => {
    const pageItems = document.getElementById('main-cart-items');
    const pageFooter = document.getElementById('main-cart-footer');
    const ids = ['cart-icon-bubble'];
    if (pageItems) ids.push(pageItems.dataset.id);
    if (pageFooter) ids.push(pageFooter.dataset.id);
    const sections = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json());
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }
    [pageItems, pageFooter].forEach((el) => {
      if (!el || !sections[el.dataset.id]) return;
      const source = parse(sections[el.dataset.id]).querySelector('.js-contents');
      const target = el.querySelector('.js-contents');
      if (source && target) target.innerHTML = source.innerHTML;
      el.closest('cart-items, #main-cart-footer')?.classList.remove('is-empty');
    });
    document.querySelector('cart-items')?.classList.remove('is-empty');
    pageFooter?.classList.remove('is-empty');
  };

  const addToCart = async (variantId) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items: [{ id: variantId, quantity: 1 }] };
    if (drawer) {
      body.sections = 'cart-drawer,cart-icon-bubble';
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || "Couldn't add to cart. Please try again in a moment.");
    if (drawer && json.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents({ id: variantId, sections: json.sections });
    } else {
      await refreshCart().catch(() => {});
      showToast(`${checkSvg}<span>Added to cart</span><a href="${DZ.cartUrl || '/cart'}">View cart</a>`);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dz-quick-add', productVariantId: variantId });
    }
  };

  /* ---------- Quick add size sheet ---------- */

  let sheet;
  let state;

  const buildSheet = () => {
    sheet = document.createElement('div');
    sheet.className = 'dz-sheet';
    sheet.innerHTML = `
      <div class="dz-sheet__backdrop" data-dz-close></div>
      <div class="dz-sheet__panel color-scheme-1 gradient" role="dialog" aria-modal="true" aria-labelledby="DzSheetTitle" tabindex="-1">
        <div class="dz-sheet__grip" aria-hidden="true"></div>
        <button type="button" class="dz-sheet__close" data-dz-close aria-label="Close">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="dz-sheet__product">
          <img class="dz-sheet__thumb" alt="" width="80" height="80">
          <div>
            <p class="dz-sheet__title" id="DzSheetTitle"></p>
            <p class="dz-sheet__price"></p>
          </div>
        </div>
        <div data-dz-groups></div>
        <button type="button" class="button button--full-width dz-sheet__add" data-dz-add disabled></button>
        <button type="button" class="dz-wl-btn dz-wl-btn--wide dz-sheet__wl" data-dz-wl="" aria-pressed="false">
          ${heartSvg}<span data-dz-wl-label>Add to wishlist</span>
        </button>
        <p class="dz-sheet__error" role="alert"></p>
        <a class="dz-sheet__link link underlined-link" data-dz-link>View full details</a>
      </div>`;
    document.body.appendChild(sheet);

    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-dz-close]')) closeSheet();
      const chip = event.target.closest('.dz-chip');
      if (chip) choose(Number(chip.dataset.option), chip.dataset.value);
      if (event.target.closest('[data-dz-add]')) addSelected();
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeSheet();
      if (event.key !== 'Tab') return;
      const focusable = [...sheet.querySelectorAll('button:not([disabled]), a[href]')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  // Only in-stock values are offered, given what's already chosen for the other options.
  const availableValues = (i) => {
    const { product, selected } = state;
    const values = [];
    product.variants.forEach((v) => {
      if (!v.available) return;
      if (!v.options.every((value, j) => j === i || selected[j] === null || selected[j] === value)) return;
      if (!values.includes(v.options[i])) values.push(v.options[i]);
    });
    return values;
  };

  const selectedVariant = () => {
    const { product, selected } = state;
    if (selected.some((value) => value === null)) return null;
    return product.variants.find((v) => v.available && v.options.every((value, j) => value === selected[j])) || null;
  };

  const renderSheet = () => {
    const { product, selected } = state;
    sheet.querySelector('[data-dz-groups]').innerHTML = product.options
      .map((name, i) => {
        const chips = availableValues(i)
          .map(
            (value) =>
              `<button type="button" class="dz-chip" role="radio" data-option="${i}" data-value="${escapeHtml(value)}" aria-checked="${selected[i] === value}">${escapeHtml(value)}</button>`
          )
          .join('');
        return `<div class="dz-sheet__group" role="radiogroup" aria-label="${escapeHtml(name)}">
          <p class="dz-sheet__legend">${escapeHtml(name)}${selected[i] ? `: <b>${escapeHtml(selected[i])}</b>` : ''}</p>
          <div class="dz-sheet__chips">${chips}</div></div>`;
      })
      .join('');

    const variant = selectedVariant();
    const prices = product.variants.filter((v) => v.available).map((v) => v.price);
    const min = Math.min(...prices);
    sheet.querySelector('.dz-sheet__price').textContent = variant
      ? formatMoney(variant.price)
      : min === Math.max(...prices)
      ? formatMoney(min)
      : `From ${formatMoney(min)}`;
    const add = sheet.querySelector('[data-dz-add]');
    if (variant) {
      add.disabled = false;
      add.textContent = `Add to cart — ${formatMoney(variant.price)}`;
    } else {
      const missing = product.options.find((name, i) => selected[i] === null);
      add.disabled = true;
      add.textContent = missing ? `Select ${missing.toLowerCase()}` : 'Sold out';
    }
  };

  const choose = (i, value) => {
    const { product, selected } = state;
    selected[i] = selected[i] === value && product.options.length > 1 ? null : value;
    selected.forEach((other, j) => {
      if (j !== i && other !== null && !availableValues(j).includes(other)) selected[j] = null;
    });
    sheet.querySelector('.dz-sheet__error').textContent = '';
    renderSheet();
    const chip = sheet.querySelector(`.dz-chip[data-option="${i}"][data-value="${CSS.escape(value)}"]`);
    if (chip) chip.focus();
  };

  const openSheet = (product, opener) => {
    if (!sheet) buildSheet();
    state = {
      product,
      opener,
      selected: product.options.map((_, i) => {
        const values = [...new Set(product.variants.filter((v) => v.available).map((v) => v.options[i]))];
        return values.length === 1 ? values[0] : null;
      }),
    };
    const thumb = sheet.querySelector('.dz-sheet__thumb');
    thumb.hidden = !product.image;
    if (product.image) thumb.src = product.image;
    sheet.querySelector('.dz-sheet__title').textContent = product.title;
    sheet.querySelector('[data-dz-link]').href = product.url;
    sheet.querySelector('.dz-sheet__error').textContent = '';
    const heart = sheet.querySelector('.dz-sheet__wl');
    heart.dataset.dzWl = product.handle;
    heart.dataset.title = product.title;
    syncWishlist();
    renderSheet();
    sheet.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => (sheet.querySelector('.dz-chip') || sheet.querySelector('.dz-sheet__panel')).focus({ preventScroll: true }));
  };

  const closeSheet = () => {
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    if (!document.querySelector('cart-drawer.active')) document.body.classList.remove('overflow-hidden');
    if (state && state.opener && document.contains(state.opener)) state.opener.focus({ preventScroll: true });
  };

  const addSelected = async () => {
    const variant = selectedVariant();
    if (!variant) return;
    const add = sheet.querySelector('[data-dz-add]');
    add.classList.add('loading');
    add.disabled = true;
    add.textContent = 'Adding…';
    try {
      await addToCart(variant.id);
      closeSheet();
    } catch (error) {
      sheet.querySelector('.dz-sheet__error').textContent = error.message;
      renderSheet();
    } finally {
      add.classList.remove('loading');
    }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-dz-qa]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    let product;
    try {
      product = JSON.parse(button.querySelector('script').textContent);
    } catch (e) {
      return;
    }
    const inStock = product.variants.filter((v) => v.available);
    if (!inStock.length) return;
    if (inStock.length === 1) {
      button.classList.add('is-loading');
      try {
        await addToCart(inStock[0].id);
      } catch (error) {
        showToast(`<span>${escapeHtml(error.message)}</span>`);
      } finally {
        button.classList.remove('is-loading');
      }
      return;
    }
    openSheet(product, button);
  });

  /* ---------- Wishlist ---------- */

  const KEY = 'drepeez_wishlist_v1';
  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => item && typeof item.h === 'string' && item.h) : [];
    } catch (e) {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 60)));
    } catch (e) {}
    syncWishlist();
  };
  const has = (handle) => read().some((item) => item.h === handle);
  const add = (handle) => write([{ h: handle, t: Date.now() }, ...read().filter((item) => item.h !== handle)]);
  const remove = (handle) => write(read().filter((item) => item.h !== handle));

  function syncWishlist() {
    const saved = new Set(read().map((item) => item.h));
    document.querySelectorAll('[data-dz-wl]').forEach((button) => {
      const on = saved.has(button.dataset.dzWl);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-saved', on);
      const title = button.dataset.title || 'this product';
      button.setAttribute('aria-label', on ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`);
      const label = button.querySelector('[data-dz-wl-label]');
      if (label) label.textContent = on ? 'Saved to wishlist' : 'Add to wishlist';
    });
    document.querySelectorAll('[data-dz-wl-count]').forEach((count) => {
      count.querySelector('span').textContent = saved.size > 99 ? '99+' : String(saved.size);
      count.hidden = saved.size === 0;
    });
  }

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-dz-wl-remove]');
    if (removeButton) {
      event.preventDefault();
      remove(removeButton.dataset.dzWlRemove);
      showToast(`${heartSvg}<span>Removed from wishlist</span>`);
      return;
    }
    const button = event.target.closest('[data-dz-wl]');
    if (!button || !button.dataset.dzWl) return;
    event.preventDefault();
    event.stopPropagation();
    if (has(button.dataset.dzWl)) {
      remove(button.dataset.dzWl);
      showToast(`${heartSvg}<span>Removed from wishlist</span>`);
    } else {
      add(button.dataset.dzWl);
      showToast(`${heartSvg}<span>Added to wishlist</span><a href="${DZ.wishlistUrl}">View</a>`);
      if (!reduceMotion.matches) {
        button.classList.remove('is-popping');
        void button.offsetWidth;
        button.classList.add('is-popping');
      }
    }
  });

  window.addEventListener('storage', (event) => event.key === KEY && syncWishlist());
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncWishlist();
    });
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- Wishlist page ---------- */

  const page = document.querySelector('[data-dz-wl-page]');
  if (page) {
    const grid = page.querySelector('[data-dz-wl-grid]');
    const setCount = () => {
      const n = grid.querySelectorAll('[data-dz-wl-item]:not(.is-removing)').length;
      page.querySelector('[data-dz-wl-page-count]').textContent = n ? `${n} saved item${n === 1 ? '' : 's'}` : '';
      page.querySelector('[data-dz-wl-empty]').hidden = n > 0;
    };
    const prune = () => {
      const saved = new Set(read().map((item) => item.h));
      grid.querySelectorAll('[data-dz-wl-item]').forEach((item) => {
        if (saved.has(item.dataset.dzWlItem) || item.classList.contains('is-removing')) return;
        item.classList.add('is-removing');
        setCount();
        setTimeout(() => item.remove(), reduceMotion.matches ? 0 : 400);
      });
    };
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-dz-wl], [data-dz-wl-remove]')) setTimeout(prune, 0);
    });
    window.addEventListener('storage', (event) => event.key === KEY && prune());

    (async () => {
      const list = read();
      if (!list.length) return setCount();
      const skeleton = page.querySelector('[data-dz-wl-skeleton]');
      skeleton.hidden = false;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const results = await Promise.all(
        list.map((item) =>
          fetch(`${root}products/${encodeURIComponent(item.h)}?section_id=dz-wishlist-card`)
            .then((res) => (res.status === 404 ? { handle: item.h, gone: true } : res.ok ? res.text().then((html) => ({ handle: item.h, html })) : { handle: item.h }))
            .catch(() => ({ handle: item.h }))
        )
      );
      // Products that were deleted are removed from the wishlist. Shopify answers a deleted
      // product's section request with an empty section (200), or sometimes a 404.
      results.forEach((result) => {
        if (result.html) result.item = parse(result.html).querySelector('[data-dz-wl-item]');
        if (result.html && !result.item) result.gone = true;
      });
      const gone = results.filter((r) => r.gone).map((r) => r.handle);
      if (gone.length) write(read().filter((item) => !gone.includes(item.h)));
      results.forEach((result) => {
        if (result.item) grid.appendChild(document.importNode(result.item, true));
      });
      skeleton.hidden = true;
      setCount();
      syncWishlist();
    })();
  }

  syncWishlist();
})();
